import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import '../../../styles/projects.css';

/**
 * ProjectCard — displays a project with an auto-cycling image slideshow
 * and a description overlay on hover.
 */
export function ProjectCard({ title, subtitle, description, tech = [], images, onClick }) {
  const [currentImg, setCurrentImg] = useState(0);

  // Auto-cycle images every 2.5s when there are multiple
  useEffect(() => {
    if (images.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentImg(i => (i + 1) % images.length);
    }, 2500);
    return () => clearInterval(timer);
  }, [images.length]);

  return (
    <article 
      className="project-card" 
      aria-label={title}
      onClick={() => onClick && onClick(images, currentImg)}
      style={{ cursor: 'pointer' }}
    >
      {/* Image */}
      <div className="project-card-image">
        <AnimatePresence mode="wait">
          <motion.img
            key={currentImg}
            src={images[currentImg]}
            alt={`${title} screenshot ${currentImg + 1}`}
            loading="lazy"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          />
        </AnimatePresence>

        {/* Description overlay on hover */}
        <div className="project-card-overlay" aria-hidden="true">
          <p className="project-overlay-text">{description}</p>
        </div>

        {/* Dot indicators */}
        {images.length > 1 && (
          <div className="project-image-dots" aria-hidden="true">
            {images.map((_, i) => (
              <span
                key={i}
                className={`project-image-dot${i === currentImg ? ' active' : ''}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="project-card-info">
        {subtitle && <span className="project-card-subtitle">{subtitle}</span>}
        <h3 className="project-card-title">{title}</h3>
        {tech.length > 0 && (
          <div className="project-card-tech" aria-label="Technologies used">
            {tech.map(t => (
              <span key={t} className="tech-tag">{t}</span>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}
